<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BatchBillingListEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    items: {
      type: Array,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getItemFromSources = (...args) => {
    const [items, fields] = args;
    const fieldNames = _.map(fields, 'name');

    let result = {};

    _.each(items, (item, i) => {
      if (i === 0) {
        result = _.pick(item, fieldNames);
      }

      for (const { name, defaultValue } of fields) {
        if (result[name] != item[name]) {
          result[name] = defaultValue;
          continue;
        }

        if (_.isUndefined(result[name])) {
          result[name] = defaultValue;
          continue;
        }

        if (result[name] != null) {
          result[name] = item[name];
        }
      }
    });

    result['$updates'] = _.reduce(fieldNames, (m, v) => _.set(m, v, false), {});
    return result;
  };
  const getPayload = (...args) => {
    const [items, item] = args;

    const updates = _.keys(_.pickBy(item.$updates, _.identity));

    if (!updates.length) {
      return;
    }

    const update = _.pick(item, updates);

    return {
      items: _.map(items, (p) => ({
        id: p.id,
        etag: p.etag,
        value: update,
      })),
    };
  };
  const syncExchangeRate = (...args) => {
    const v = exchange_rate.value;
    item.value.exchange_rate = v.value == null ? null : v;
  };

  const isReadonly = ref(false);
  const exchange_rate = ref(null);
  const exchangeRateSources = ref(null);
  const editableFields = ref([
    {
      name: 'fulfillment_date',
    },
    {
      name: 'due_date',
    },
    {
      name: 'currency',
    },
    {
      name: 'exchange_rate',
    },
    {
      name: 'billing_period',
      defaultValue: {
        start: null,
        end: null,
      },
    },
  ]);
  const labelAlign = ref('right');
  const formId = ref(null);
  const formName = ref('BatchBillingListEdit');
  const item = ref(null);
  const currencies = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    isReadonly: isReadonly.value,
    exchange_rate: exchange_rate.value,
    exchangeRateSources: exchangeRateSources.value,
    editableFields: editableFields.value,
    labelAlign: labelAlign.value,
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    currencies: currencies.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_exchange_rate = async () => {
    return { value: null, date: null, source: null };
  };
  const $$load_exchangeRateSources = async () => {
    return $app.value.repo.get('ui_metadata', {
      path: 'exchange_rate_source_options',
    });
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return getItemFromSources(props.items, $data.value.editableFields);
  };
  const $$load_currencies = async () => {
    return $app.value.repo.get('list_currency');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'isReadonly' },
      { name: 'exchange_rate' },
      { name: 'exchangeRateSources' },
      { name: 'editableFields', dependsOn: ['exchange_rate'] },
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'currencies' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      exchange_rate: $$load_exchange_rate,
      exchangeRateSources: $$load_exchangeRateSources,
      formId: $$load_formId,
      item: $$load_item,
      currencies: $$load_currencies,
    },
    ux: {},
    dataRefs: {
      isReadonly,
      exchange_rate,
      exchangeRateSources,
      editableFields,
      labelAlign,
      formId,
      formName,
      item,
      currencies,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'BatchBillingListEditor|Title',
        'Batch Edit Billint List Items'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_kdbsu3oad_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_ew38wjwod_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_5otofge0t_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_billing_list_item',
      data: getPayload(props.items, item.value),
    };
  };
  const act_4397690859646801_script_qobc2xl1m_value_callable = async (
    $event
  ) => {
    return syncExchangeRate();
  };
  const act_4397690859646801_script_qobc2xl1m_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4397690859646801_script_qobc2xl1m_value_callable($event);
      },
    };
  };
  const act_4067533979448964_script_bbrvbsytg_value_callable = async (
    $event
  ) => {
    return syncExchangeRate();
  };
  const act_4067533979448964_script_bbrvbsytg_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4067533979448964_script_bbrvbsytg_value_callable($event);
      },
    };
  };
  const act_8826227742204642_script_axrkz54mr_value_callable = async (
    $event
  ) => {
    return syncExchangeRate();
  };
  const act_8826227742204642_script_axrkz54mr_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8826227742204642_script_axrkz54mr_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_kdbsu3oad =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_kdbsu3oad_getActionArgs,
    });
  const inst_evh_7315092382398562_close_kqb1sxfck =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_kdbsu3oad,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_kqb1sxfck],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_q6b2hns9l =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_s7avnko2v =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_q6b2hns9l,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_s7avnko2v],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_wn78x41df =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_62c71dv6a =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_wn78x41df,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_62c71dv6a],
    event: 'close',
  });
  const act_1040167445267876_closeModal_ew38wjwod =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_ew38wjwod_getActionArgs,
    });
  const inst_evh_1040167445267876_success_6p21qvlzv =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_ew38wjwod,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_5otofge0t = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_5otofge0t_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_6p21qvlzv],
  });
  const inst_evh_1040167445267876_submit_wqn437238 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_5otofge0t,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_wqn437238],
    event: 'submit',
  });
  const act_4397690859646801_script_qobc2xl1m = new actionTypes.ScriptAction({
    actionArgs: act_4397690859646801_script_qobc2xl1m_getActionArgs,
  });
  const inst_evh_4397690859646801_blur_3tbfslass =
    new actionTypes.EventHandlerImpl({
      action: act_4397690859646801_script_qobc2xl1m,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_4397690859646801_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4397690859646801_blur_3tbfslass],
    event: 'blur',
  });
  const act_4067533979448964_script_bbrvbsytg = new actionTypes.ScriptAction({
    actionArgs: act_4067533979448964_script_bbrvbsytg_getActionArgs,
  });
  const inst_evh_4067533979448964_blur_or6b9v58n =
    new actionTypes.EventHandlerImpl({
      action: act_4067533979448964_script_bbrvbsytg,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_4067533979448964_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4067533979448964_blur_or6b9v58n],
    event: 'blur',
  });
  const act_8826227742204642_script_axrkz54mr = new actionTypes.ScriptAction({
    actionArgs: act_8826227742204642_script_axrkz54mr_getActionArgs,
  });
  const inst_evh_8826227742204642_blur_vorqapvwe =
    new actionTypes.EventHandlerImpl({
      action: act_8826227742204642_script_axrkz54mr,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_8826227742204642_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8826227742204642_blur_vorqapvwe],
    event: 'blur',
  });
  const act_2434514588044777_closeComponent_3g4xbtrco =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_1na96svts =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_3g4xbtrco,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_1na96svts],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_kqb1sxfck,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_s7avnko2v,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_62c71dv6a,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_6p21qvlzv,
    inst_evh_1040167445267876_submit_wqn437238,
    save: inst_evh_1040167445267876_submit_wqn437238,
    inst_evh_1040167445267876_submit,
    inst_evh_4397690859646801_blur_3tbfslass,
    inst_evh_4397690859646801_blur,
    inst_evh_4067533979448964_blur_or6b9v58n,
    inst_evh_4067533979448964_blur,
    inst_evh_8826227742204642_blur_vorqapvwe,
    inst_evh_8826227742204642_blur,
    inst_evh_2434514588044777_clickPreventStop_1na96svts,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_kqb1sxfck = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_kqb1sxfck.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_s7avnko2v = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_s7avnko2v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_62c71dv6a = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_62c71dv6a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_6p21qvlzv = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_6p21qvlzv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_wqn437238 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_wqn437238.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4397690859646801_blur_3tbfslass = async (event, scope) => {
    return await actions.inst_evh_4397690859646801_blur_3tbfslass.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4397690859646801_blur = async (event, scope) => {
    return await actions.inst_evh_4397690859646801_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4067533979448964_blur_or6b9v58n = async (event, scope) => {
    return await actions.inst_evh_4067533979448964_blur_or6b9v58n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4067533979448964_blur = async (event, scope) => {
    return await actions.inst_evh_4067533979448964_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8826227742204642_blur_vorqapvwe = async (event, scope) => {
    return await actions.inst_evh_8826227742204642_blur_vorqapvwe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8826227742204642_blur = async (event, scope) => {
    return await actions.inst_evh_8826227742204642_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_1na96svts = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_1na96svts.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BatchBillingListEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="fulfillment_date"
          :label="gettext('Fulfillment Date')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
          contentClass="input-group"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="fulfillment_date_enabled"
              v-model="item.$updates.fulfillment_date"
              :value="item.$updates.fulfillment_date"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.fulfillment_date =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormDatetime
            name="fulfillment_date"
            v-model="item.fulfillment_date"
            :disabled="!item.$updates.fulfillment_date"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="due_date"
          :label="pgettext('Invoice', 'Due Date')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
          contentClass="input-group"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="due_date_enabled"
              v-model="item.$updates.due_date"
              :value="item.$updates.due_date"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.due_date =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormDatetime
            class="w-auto"
            name="due_date"
            v-model="item.due_date"
            :disabled="!item.$updates.due_date"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="billing_period"
          :label="gettext('Billing Period')"
          contentClass="input-group"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          v-if="item.billing_period"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="billing_period_enabled"
              v-model="item.$updates.billing_period"
              :value="item.$updates.billing_period"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.billing_period =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <div class="row">
            <div class="col-4">
              <ItpFormDatetime
                name="billing_period_start"
                v-model="item.billing_period.start"
                :placeholder="pgettext('BillingPeriod', 'From')"
                :disabled="!item.$updates.billing_period"
              >
              </ItpFormDatetime>
            </div>
            <div class="col-4">
              <ItpFormDatetime
                name="billing_period_end"
                v-model="item.billing_period.end"
                :placeholder="pgettext('BillingPeriod', 'To')"
                :disabled="!item.$updates.billing_period"
              >
              </ItpFormDatetime>
            </div>
          </div>
        </ItpFormGroup>
        <ItpFormGroup
          name="currency"
          :label="pgettext('Invoice', 'Currency')"
          :label-cols-md="4"
          :content-cols-md="3"
          :labelAlign="labelAlign"
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="currency_enabled"
              v-model="item.$updates.currency"
              :value="item.$updates.currency"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.currency =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormSelect
            name="currency"
            v-model="item.currency"
            :value="item.currency"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.currency =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="currencies"
            valueField="code"
            textField="code"
            :disabled="!item.$updates.currency"
          >
          </ItpFormSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="currency"
          :label="pgettext('Invoice', 'Exchange rate')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="exchange_rate_enabled"
              v-model="item.$updates.exchange_rate"
              :value="item.$updates.exchange_rate"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.exchange_rate =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <div class="row">
            <div class="col-3">
              <ItpFormInput
                mask="number"
                unmask="typed"
                :scale="2"
                name="exchange_rate"
                v-model="exchange_rate.value"
                :value="exchange_rate.value"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (exchange_rate.value =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :readonly="isReadonly"
                :placeholder="pgettext('Invoice', 'Rate')"
                :disabled="!item.$updates.exchange_rate"
                @blur="evh_4397690859646801_blur($event, {})"
              >
              </ItpFormInput>
            </div>
            <div class="col-4">
              <ItpFormDatetime
                name="exchange_rate_date"
                v-model="exchange_rate.date"
                :readonly="isReadonly"
                :placeholder="pgettext('Invoice', 'Date')"
                :disabled="!item.$updates.exchange_rate"
                @blur="evh_4067533979448964_blur($event, {})"
              >
              </ItpFormDatetime>
            </div>
            <div class="col-5">
              <ItpFormSelect
                name="exchange_rate_source"
                v-model="exchange_rate.source"
                :value="exchange_rate.source"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (exchange_rate.source =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :options="exchangeRateSources"
                :readonly="isReadonly"
                :placeholder="pgettext('ExchangeRateFieldset', 'Source')"
                valueField="value"
                textField="name"
                null-option
                :disabled="!item.$updates.exchange_rate"
                @blur="evh_8826227742204642_blur($event, {})"
              >
              </ItpFormSelect>
            </div>
          </div>
        </ItpFormGroup>
        <hr class="my-4" />
        <ItpFormGroup
          name="notes"
          :label="pgettext('Invoice', 'Notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="notes_enabled"
              v-model="item.$updates.notes"
              :value="item.$updates.notes"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.notes =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormTextarea
            name="notes"
            v-model="item.notes"
            :value="item.notes"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.notes =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.notes"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
